/**
 * TeethChart.jsx
 *
 * A controlled React component for annotating a dental tooth chart.
 *
 * Props:
 *   value      – Record<toothNumber, ToothData> | undefined
 *                Initial/controlled state, keyed by tooth number (Universal system, 1–32).
 *                ToothData: { condition?: string; note?: string; surfaces?: string[] }
 *
 *   onChange   – (value: Record<string, ToothData>) => void
 *                Called whenever any tooth annotation changes.
 *
 *   readOnly   – (optional) When true, the chart is display-only: teeth can be clicked to
 *                inspect their annotations but nothing can be edited or saved.
 *
 *   conditions – (optional) Array of condition config objects to override the defaults.
 *                Each: { key: string; label: string; color: string }
 *
 * Usage:
 *   import TeethChart from './TeethChart';
 *
 *   const [chart, setChart] = useState({});
 *
 *   <TeethChart value={chart} onChange={setChart} />
 *
 * Tooth numbering: Universal Numbering System (1–32).
 *   Upper right → upper left: 1–16
 *   Lower left  → lower right: 17–32
 */

import { useState, useEffect, useCallback } from 'react'
import { Table } from '@chakra-ui/react'
import {
  Button,
  Card,
  Stack,
  Box,
  Text,
  Textarea,
  SimpleTabs,
  Wrap,
  WrapItem
} from '@/components/lib'

// ---------------------------------------------------------------------------
// Default condition palette
// ---------------------------------------------------------------------------
const DEFAULT_CONDITIONS = [
  { key: 'healthy', label: 'Healthy', color: '#22c55e' },
  { key: 'cavity', label: 'Cavity', color: '#ef4444' },
  { key: 'filling', label: 'Filling', color: '#3b82f6' },
  { key: 'crown', label: 'Crown', color: '#f59e0b' },
  { key: 'missing', label: 'Missing', color: '#9ca3af' },
  { key: 'rct', label: 'Root canal', color: '#a855f7' },
  { key: 'implant', label: 'Implant', color: '#06b6d4' },
  { key: 'bridge', label: 'Bridge', color: '#f97316' }
]

// ---------------------------------------------------------------------------
// Tooth geometry helpers (Universal numbering, 1–32)
// ---------------------------------------------------------------------------

// Upper arch:  1–16  (right molars → front → left molars)
// Lower arch: 17–32  (left molars → front → right molars)
const UPPER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]
const LOWER = [32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17]

const MOLARS = new Set([1, 2, 3, 14, 15, 16, 17, 18, 19, 30, 31, 32])
const PREMOLARS = new Set([4, 5, 12, 13, 20, 21, 28, 29])
// Everything else is incisors/canines

function toothSize (n) {
  if (MOLARS.has(n)) return { w: 22, h: 26, rx: 5 }
  if (PREMOLARS.has(n)) return { w: 17, h: 24, rx: 5 }
  return { w: 13, h: 26, rx: 6 }
}

// The 5 surfaces of a tooth (for surface-level annotation)
// Rendered as small squares inside the tooth shape
const SURFACES = ['O', 'M', 'D', 'B', 'L'] // Occlusal, Mesial, Distal, Buccal, Lingual

// Surface label long names
const SURFACE_LABELS = { O: 'Occlusal', M: 'Mesial', D: 'Distal', B: 'Buccal', L: 'Lingual' }

// Surface positions within a tooth cell (as fractions of cell w/h)
function surfacePositions (w, h) {
  const cx = w / 2; const cy = h / 2
  const r = Math.min(w, h) * 0.18
  return {
    O: { x: cx, y: cy }, // centre
    M: { x: r + 1, y: cy }, // left
    D: { x: w - r - 1, y: cy }, // right
    B: { x: cx, y: r + 1 }, // top
    L: { x: cx, y: h - r - 1 } // bottom
  }
}

// ---------------------------------------------------------------------------
// ToothSVG – draws one tooth
// ---------------------------------------------------------------------------
function ToothSVG ({ number, data, conditionColor, isSelected, onClick }) {
  const { w, h, rx } = toothSize(number)

  const baseFill = conditionColor ?? '#f9fafb'
  const baseStroke = isSelected ? '#1d4ed8' : (conditionColor ? darken(conditionColor, 0.3) : '#d1d5db')
  const strokeW = isSelected ? 2.5 : 1

  const surfaces = data?.surfaces ?? []
  const positions = surfacePositions(w, h)

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      style={{ cursor: onClick ? 'pointer' : 'default', display: 'block', overflow: 'visible' }}
      onClick={onClick}
      aria-label={`Tooth ${number}${data?.condition ? `, ${data.condition}` : ''}`}
    >
      {/* Tooth body */}
      <rect
        x={1} y={1} width={w - 2} height={h - 2}
        rx={rx} ry={rx}
        fill={baseFill}
        stroke={baseStroke}
        strokeWidth={strokeW}
      />

      {/* Surface dots */}
      {SURFACES.map((s) => {
        const active = surfaces.includes(s)
        if (!active) return null
        const pos = positions[s]
        return (
          <circle
            key={s}
            cx={pos.x} cy={pos.y} r={Math.min(w, h) * 0.13}
            fill={darken(conditionColor ?? '#6b7280', 0.2)}
            opacity={0.85}
          />
        )
      })}

      {/* Missing X */}
      {data?.condition === 'missing' && (
        <>
          <line x1={4} y1={4} x2={w - 4} y2={h - 4} stroke='#9ca3af' strokeWidth={1.5} />
          <line x1={w - 4} y1={4} x2={4} y2={h - 4} stroke='#9ca3af' strokeWidth={1.5} />
        </>
      )}

      {/* Selected ring */}
      {isSelected && (
        <rect
          x={-1} y={-1} width={w + 2} height={h + 2}
          rx={rx + 1} ry={rx + 1}
          fill='none'
          stroke='#1d4ed8'
          strokeWidth={1}
          opacity={0.4}
        />
      )}
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Tiny colour helper – darken a hex colour
// ---------------------------------------------------------------------------
function darken (hex, amount = 0.2) {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, ((n >> 16) & 255) - Math.round(255 * amount))
  const g = Math.max(0, ((n >> 8) & 255) - Math.round(255 * amount))
  const b = Math.max(0, (n & 255) - Math.round(255 * amount))
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

function hexToRgba (hex, alpha = 0.15) {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = (n >> 16) & 255; const g = (n >> 8) & 255; const b = n & 255
  return `rgba(${r},${g},${b},${alpha})`
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function TeethChart ({
  value,
  onChange,
  readOnly = false,
  conditions: conditionsProp
}) {
  const conditions = conditionsProp ?? DEFAULT_CONDITIONS

  const [chart, setChart] = useState(value ?? {})
  const [selected, setSelected] = useState(null)
  const [draft, setDraft] = useState({ condition: null, surfaces: [], note: '' })
  const [activeTab, setActiveTab] = useState('chart')

  useEffect(() => {
    if (value !== undefined) setChart(value)
  }, [value])

  const conditionColor = useCallback(
    (key) => conditions.find((c) => c.key === key)?.color,
    [conditions]
  )

  function selectTooth (n) {
    setSelected(n)
    const existing = chart[n]
    setDraft({
      condition: existing?.condition ?? null,
      surfaces: existing?.surfaces ?? [],
      note: existing?.note ?? ''
    })
  }

  function toggleSurface (s) {
    setDraft((d) => ({
      ...d,
      surfaces: d.surfaces.includes(s)
        ? d.surfaces.filter((x) => x !== s)
        : [...d.surfaces, s]
    }))
  }

  function saveDraft () {
    if (selected === null) return
    const updated = { ...chart }
    if (!draft.condition && !draft.note && draft.surfaces.length === 0) {
      delete updated[selected]
    } else {
      updated[selected] = {
        ...(draft.condition ? { condition: draft.condition } : {}),
        ...(draft.surfaces.length ? { surfaces: draft.surfaces } : {}),
        ...(draft.note.trim() ? { note: draft.note.trim() } : {})
      }
    }
    setChart(updated)
    onChange?.(updated)
    setSelected(null)
  }

  function clearTooth () {
    if (selected === null) return
    const updated = { ...chart }
    delete updated[selected]
    setChart(updated)
    onChange?.(updated)
    setDraft({ condition: null, surfaces: [], note: '' })
    setSelected(null)
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  function renderArch (numbers, labelPosition) {
    return (
      <Stack direction='row' justify='center' gap='3px' flexWrap='nowrap'>
        {numbers.map((n) => {
          const data = chart[n]
          const color = data?.condition ? conditionColor(data.condition) : undefined
          return (
            <Stack key={n} align='center' gap='2px'>
              {labelPosition === 'top' && (
                <Text fontSize='9px' color='fg.subtle' lineHeight={1}>{n}</Text>
              )}
              <ToothSVG
                number={n}
                data={data}
                conditionColor={color}
                isSelected={selected === n}
                onClick={readOnly && !data ? undefined : () => selectTooth(n)}
              />
              {labelPosition === 'bottom' && (
                <Text fontSize='9px' color='fg.subtle' lineHeight={1}>{n}</Text>
              )}
            </Stack>
          )
        })}
      </Stack>
    )
  }

  const annotatedTeeth = Object.entries(chart).sort(([a], [b]) => Number(a) - Number(b))

  // -------------------------------------------------------------------------
  // Tab content
  // -------------------------------------------------------------------------
  const chartContent = (
    <>
      {/* Chart area */}
      <Stack align='center' gap={0} mb={3}>
        {renderArch(UPPER, 'top')}
        <Stack direction='row' align='center' gap={2} my='5px' w='100%' justify='center'>
          <Text fontSize='10px' color='fg.subtle' textTransform='uppercase' letterSpacing='wider'>upper</Text>
          <Box flex={1} maxW='320px' h='1px' bg='border.default' />
          <Text fontSize='10px' color='fg.subtle' textTransform='uppercase' letterSpacing='wider'>lower</Text>
        </Stack>
        {renderArch(LOWER, 'bottom')}
      </Stack>

      {/* Legend */}
      <Wrap gap={2} justify='center' mb={4}>
        {conditions.map((c) => (
          <WrapItem key={c.key}>
            <Stack direction='row' align='center' gap={1}>
              <Box w='8px' h='8px' borderRadius='full' style={{ background: c.color }} />
              <Text fontSize='xs' color='fg.muted'>{c.label}</Text>
            </Stack>
          </WrapItem>
        ))}
      </Wrap>

      {/* Editor / detail panel */}
      <Card>
        {selected === null
          ? (
            <Text textAlign='center' color='fg.muted' py={5}>
              {readOnly ? 'Click an annotated tooth to view its details' : 'Click a tooth to annotate it'}
            </Text>
            )
          : readOnly
            ? (
              /* Read-only detail panel */
              <Stack gap={3}>
                <Stack direction='row' justify='space-between' align='center'>
                  <Text fontWeight='semibold'>Tooth {selected}</Text>
                  <Button variant='ghost' size='xs' onClick={() => setSelected(null)}>Close</Button>
                </Stack>

                <Box>
                  <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='fg.muted' mb={2}>Condition</Text>
                  {draft.condition
                    ? (() => {
                        const col = conditionColor(draft.condition)
                        return (
                          <Box
                            as='span'
                            display='inline-flex'
                            alignItems='center'
                            gap={1}
                            px={3}
                            py='4px'
                            borderRadius='full'
                            borderWidth='1px'
                            fontSize='sm'
                            style={{
                              background: hexToRgba(col, 0.2),
                              borderColor: col,
                              color: darken(col, 0.1)
                            }}
                          >
                            <Box as='span' w='7px' h='7px' borderRadius='full' display='inline-block' style={{ background: col }} />
                            {conditions.find((c) => c.key === draft.condition)?.label ?? draft.condition}
                          </Box>
                        )
                      })()
                    : <Text color='fg.muted' fontSize='sm'>—</Text>}
                </Box>

                <Box>
                  <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='fg.muted' mb={2}>Affected surfaces</Text>
                  <Stack direction='row' gap={2}>
                    {SURFACES.map((s) => {
                      const active = draft.surfaces.includes(s)
                      const col = draft.condition ? conditionColor(draft.condition) : '#6b7280'
                      return (
                        <Box
                          key={s}
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          w='34px'
                          h='30px'
                          borderRadius='md'
                          borderWidth='1px'
                          fontSize='xs'
                          fontWeight='semibold'
                          title={SURFACE_LABELS[s]}
                          style={{
                            background: active ? hexToRgba(col, 0.2) : 'transparent',
                            borderColor: active ? col : 'var(--chakra-colors-border-default)',
                            color: active ? darken(col, 0.15) : 'var(--chakra-colors-fg-muted)'
                          }}
                        >
                          {s}
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>

                <Box>
                  <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='fg.muted' mb={2}>Notes</Text>
                  <Textarea
                    rows={3}
                    value={draft.note}
                    readOnly
                    placeholder='No notes recorded'
                  />
                </Box>
              </Stack>
              )
            : (
              /* Editable panel */
              <Stack gap={3}>
                <Stack direction='row' justify='space-between' align='center'>
                  <Text fontWeight='semibold'>Tooth {selected}</Text>
                  <Button variant='ghost' colorPalette='red' size='xs' onClick={clearTooth}>Clear</Button>
                </Stack>

                {/* Condition pills */}
                <Wrap gap={2}>
                  {conditions.map((c) => {
                    const active = draft.condition === c.key
                    return (
                      <WrapItem key={c.key}>
                        <Button
                          size='sm'
                          variant='outline'
                          borderRadius='full'
                          style={{
                            background: active ? hexToRgba(c.color, 0.2) : 'transparent',
                            borderColor: active ? c.color : undefined,
                            color: active ? darken(c.color, 0.1) : undefined,
                            fontWeight: active ? 600 : 400
                          }}
                          onClick={() =>
                            setDraft((d) => ({
                              ...d,
                              condition: d.condition === c.key ? null : c.key
                            }))}
                        >
                          <Box
                            as='span'
                            w='7px'
                            h='7px'
                            borderRadius='full'
                            display='inline-block'
                            mr={1}
                            style={{ background: c.color, opacity: active ? 1 : 0.4 }}
                          />
                          {c.label}
                        </Button>
                      </WrapItem>
                    )
                  })}
                </Wrap>

                <Box>
                  <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='fg.muted' mb={2}>
                    Affected surfaces
                  </Text>
                  <Stack direction='row' gap={2}>
                    {SURFACES.map((s) => {
                      const active = draft.surfaces.includes(s)
                      const col = draft.condition ? conditionColor(draft.condition) : '#6b7280'
                      return (
                        <Button
                          key={s}
                          size='sm'
                          variant='outline'
                          title={SURFACE_LABELS[s]}
                          style={{
                            width: '34px',
                            minWidth: 'unset',
                            padding: 0,
                            background: active ? hexToRgba(col, 0.2) : 'transparent',
                            borderColor: active ? col : undefined,
                            color: active ? darken(col, 0.15) : undefined
                          }}
                          onClick={() => toggleSurface(s)}
                        >
                          {s}
                        </Button>
                      )
                    })}
                  </Stack>
                </Box>

                <Box>
                  <Text fontSize='xs' fontWeight='semibold' textTransform='uppercase' letterSpacing='wider' color='fg.muted' mb={2}>
                    Notes
                  </Text>
                  <Textarea
                    rows={3}
                    placeholder={`Clinical notes for tooth ${selected}…`}
                    value={draft.note}
                    onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                  />
                </Box>

                <Stack direction='row' justify='flex-end' gap={2}>
                  <Button variant='ghost' onClick={() => setSelected(null)}>Cancel</Button>
                  <Button colorPalette='primary' onClick={saveDraft}>Save</Button>
                </Stack>
              </Stack>
              )}
      </Card>
    </>
  )

  const summaryContent = (
    <Card>
      {annotatedTeeth.length === 0
        ? (
          <Text textAlign='center' color='fg.muted' py={5}>No teeth annotated yet.</Text>
          )
        : (
          <Table.Root size='sm'>
            <Table.Header>
              <Table.Row bg='bg.subtle'>
                {['Tooth', 'Condition', 'Surfaces', 'Notes'].map((h) => (
                  <Table.ColumnHeader key={h} color='fg.muted' fontWeight='medium'>{h}</Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {annotatedTeeth.map(([n, d]) => {
                const col = d.condition ? conditionColor(d.condition) : null
                return (
                  <Table.Row
                    key={n}
                    cursor='pointer'
                    onClick={() => { setActiveTab('chart'); selectTooth(Number(n)) }}
                  >
                    <Table.Cell><Text fontWeight='semibold'>{n}</Text></Table.Cell>
                    <Table.Cell>
                      {d.condition
                        ? (
                          <Box
                            as='span'
                            display='inline-block'
                            px={2}
                            py='2px'
                            borderRadius='full'
                            borderWidth='1px'
                            fontSize='xs'
                            fontWeight='medium'
                            style={{
                              background: hexToRgba(col, 0.15),
                              color: darken(col, 0.1),
                              borderColor: hexToRgba(col, 0.4)
                            }}
                          >
                            {conditions.find((c) => c.key === d.condition)?.label ?? d.condition}
                          </Box>
                          )
                        : '—'}
                    </Table.Cell>
                    <Table.Cell>{d.surfaces?.length ? d.surfaces.join(', ') : '—'}</Table.Cell>
                    <Table.Cell><Text fontSize='sm' color='fg.muted'>{d.note || '—'}</Text></Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
          )}
    </Card>
  )

  return (
    <Box maxW='680px' mx='auto'>
      <SimpleTabs
        value={activeTab}
        onValueChange={setActiveTab}
        tabs={[
          { key: 'chart', title: 'Chart', content: chartContent },
          { key: 'summary', title: `Summary (${annotatedTeeth.length})`, content: summaryContent }
        ]}
      />
    </Box>
  )
}
